import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { C, FONT, R, SP } from '../theme/tokens';
import { CALC_REGISTRY, CalcCategory, CalcEntry } from '../utils/calcRegistry';

type Nav = {
  navigate: (screen: string, params?: any) => void;
};

const CATEGORY_LABEL: Record<CalcCategory, string> = {
  TAB: 'TAB',
  Cleanroom: 'CLR',
  Fumehood: 'FUM',
  BET: 'BET',
};

function tokenize(s: string): string[] {
  return s.toLowerCase().split(/\s+/).filter(Boolean);
}

function scoreEntry(entry: CalcEntry, tokens: string[]): number {
  if (tokens.length === 0) return 1;
  const haystack = `${entry.title} ${entry.category} ${entry.route}`.toLowerCase();
  const title = entry.title.toLowerCase();
  let score = 0;
  for (const t of tokens) {
    if (!haystack.includes(t)) return 0;
    if (title.startsWith(t)) score += 3;
    else if (title.includes(t)) score += 2;
    else score += 1;
  }
  return score;
}

export default function CalcSearchScreen({ navigation }: { navigation: Nav }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const results = useMemo(() => {
    const tokens = tokenize(query);
    if (tokens.length === 0) return CALC_REGISTRY.slice();
    return CALC_REGISTRY
      .map((entry) => ({ entry, score: scoreEntry(entry, tokens) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title))
      .map((r) => r.entry);
  }, [query]);

  const open = (entry: CalcEntry) => {
    navigation.navigate(entry.category, { screen: entry.route });
  };

  return (
    <View style={styles.container}>
      <View style={styles.inner}>
        <View style={styles.header}>
          <View style={styles.eyebrowRow}>
            <View style={styles.eyebrowBar} />
            <Text style={styles.eyebrowText}>SEARCH</Text>
          </View>
          <Text style={styles.title}>Find a calculator</Text>
          <Text style={styles.subtitle}>
            {CALC_REGISTRY.length} calculators across TAB, Cleanroom, Fume Hood,
            and Building Envelope Testing.
          </Text>
        </View>

        <View style={styles.search}>
          <Ionicons name="search" size={14} color={C.dim} />
          <TextInput
            ref={inputRef}
            value={query}
            onChangeText={setQuery}
            placeholder="Search by name, category, or keyword"
            placeholderTextColor={C.dim}
            style={styles.searchInput}
            autoCorrect={false}
            autoCapitalize="none"
            returnKeyType="search"
            onSubmitEditing={() => {
              if (results.length > 0) open(results[0]);
            }}
          />
          {query.length > 0 ? (
            <Pressable
              onPress={() => setQuery('')}
              style={({ pressed }) => [styles.clearInline, pressed && styles.pressed]}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              hitSlop={8}
            >
              <Ionicons name="close-circle" size={16} color={C.dim} />
            </Pressable>
          ) : null}
        </View>

        <Text style={styles.countLine}>
          {results.length} match{results.length === 1 ? '' : 'es'}
        </Text>

        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {results.length === 0 ? (
            <View style={styles.emptyCard}>
              <Text style={styles.emptyTitle}>No calculators match</Text>
              <Text style={styles.emptyText}>
                Try a different keyword, or search by category (TAB, Cleanroom,
                Fume Hood, BET).
              </Text>
            </View>
          ) : (
            results.map((entry) => (
              <Pressable
                key={`${entry.category}-${entry.route}`}
                style={({ pressed }) => [styles.row, pressed && styles.pressed]}
                onPress={() => open(entry)}
                accessibilityRole="button"
                accessibilityLabel={`Open ${entry.title}`}
              >
                <View style={styles.rowMain}>
                  <Text style={styles.rowCategory}>
                    {CATEGORY_LABEL[entry.category]}
                  </Text>
                  <Text style={styles.rowTitle} numberOfLines={2}>
                    {entry.title}
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={16} color={C.dim} />
              </Pressable>
            ))
          )}
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg },
  inner: {
    flex: 1,
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
    paddingHorizontal: SP.md,
    paddingTop: SP.md,
  },

  header: { marginBottom: SP.md },
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', marginBottom: SP.xs },
  eyebrowBar: {
    width: 4,
    height: 11,
    backgroundColor: C.accent,
    borderRadius: 1,
    marginRight: SP.sm,
  },
  eyebrowText: { fontFamily: FONT.mono, fontSize: 11, color: C.dim, letterSpacing: 0.8 },
  title: { fontFamily: FONT.sansBold, fontSize: 26, color: C.ink, letterSpacing: -0.3 },
  subtitle: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    marginTop: SP.xs + 2,
    lineHeight: 19,
  },

  search: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm,
    backgroundColor: C.panel,
    borderRadius: R.field,
    borderWidth: 1,
    borderColor: C.edge,
    paddingHorizontal: SP.sm + 4,
    height: 44,
    marginBottom: SP.sm,
  },
  searchInput: {
    flex: 1,
    fontFamily: FONT.sans,
    fontSize: 15,
    color: C.ink,
    paddingVertical: 0,
  },
  clearInline: { padding: 2 },
  pressed: { opacity: 0.85 },

  countLine: {
    fontFamily: FONT.mono,
    fontSize: 11,
    color: C.dim,
    letterSpacing: 0.6,
    marginBottom: SP.sm,
  },

  scroll: { flex: 1 },
  scrollContent: { paddingBottom: SP.xl },

  emptyCard: {
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    padding: SP.lg,
    alignItems: 'center',
  },
  emptyTitle: {
    fontFamily: FONT.sansMed,
    fontSize: 15,
    color: C.ink,
    marginBottom: SP.xs,
  },
  emptyText: {
    fontFamily: FONT.sans,
    fontSize: 13,
    color: C.ink2,
    textAlign: 'center',
    lineHeight: 19,
  },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm,
    backgroundColor: C.panel,
    borderRadius: R.card,
    borderWidth: 1,
    borderColor: C.edge,
    paddingHorizontal: SP.md,
    paddingVertical: SP.sm + 4,
    marginBottom: SP.sm,
  },
  rowMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SP.sm + 4,
  },
  rowCategory: {
    fontFamily: FONT.mono,
    fontSize: 10,
    color: C.dim,
    letterSpacing: 0.8,
    width: 34,
  },
  rowTitle: {
    flex: 1,
    fontFamily: FONT.sansMed,
    fontSize: 15,
    color: C.ink,
  },
});
